import { map } from "lodash";
import { Select } from "orcs-design-system";
import PropTypes from "prop-types";
import { useCallback, useMemo, useState } from "react";
import { useTheme } from "styled-components";

import { TagTypeConfigPropType } from "../propTypes";
import {
  canAddMultiTags,
  createTagSelectorOption,
  createSelectableTagOptions,
  canCreateNewTags,
} from "../TagsEditor.util";
import useSelectedValues from "../useSelectedValues";

import { SelectorContext } from "../contexts/selectorContext";
import CustomisedComponents, { CustomMenuList } from "./TagSelector.components";
import updateStyles from "./TagSelector.styles";
import { portaledTagSelectorMenuStyles } from "./portaledTagSelectorMenu";
import { useTagSelectorActions } from "./TagSelector.actions";
import { tagSelectorControlSelectProps } from "./TagSelector.select";

// TODO: Consolidate with src/pages/Workspaces/components/TagTypeConfig/TagAttributeFields/TagSelector/TagSelector.js

const useTagSelectorState = ({
  loading,
  existingTags,
  allTags,
  setNewTag,
  tagTypesConfig,
  removeTag,
  onBlur,
  onEntityTagClick,
  setError,
  helpIsOpen,
  menuIsOpen,
  inputValue,
  isAssociationTag,
  onEditTagAttributes,
  onCreateNewTag,
  bulkTagMode,
  entityType,
  loadOptions,
  clearSearchTerm,
  menuPortalTarget,
  hideDropdown,
}) => {
  const theme = useTheme();
  const { addTag, tags } = useSelectedValues(existingTags, entityType);
  const [tagsUpdating, setTagsUpdating] = useState({});
  const [typedValue, setTypedValue] = useState(inputValue);
  const [openMenu, setOpenMenu] = useState(menuIsOpen);
  const options = useMemo(
    () => createSelectableTagOptions(allTags, tagTypesConfig, existingTags),
    [allTags, tagTypesConfig, existingTags]
  );

  const existingTagValuesAsOptions = useMemo(
    () => map(tags, createTagSelectorOption),
    [tags]
  );

  const applySelectStyles = useCallback(
    (customStyles) => {
      const styles = updateStyles(customStyles, theme);
      return menuPortalTarget ? portaledTagSelectorMenuStyles(styles) : styles;
    },
    [menuPortalTarget, theme]
  );

  const handleInputChange = useCallback(
    (value, action) => {
      if (hideDropdown) {
        return;
      }
      if (action.action !== "input-blur" && action.action !== "menu-close") {
        setTypedValue(value);
        setOpenMenu(!!value);
        loadOptions(value);
      }
    },
    [loadOptions, hideDropdown]
  );

  const handleMenuOpen = useCallback(() => {
    if (!hideDropdown) {
      setOpenMenu(true);
    }
  }, [setOpenMenu, hideDropdown]);

  const handleMenuClose = useCallback(() => {
    if (!helpIsOpen) {
      setOpenMenu(false);
      setTypedValue("");
    }
  }, [helpIsOpen, setOpenMenu, setTypedValue]);

  const isMulti = canAddMultiTags(tagTypesConfig);
  const { onChange, onRemoveTag, onAddTag } = useTagSelectorActions({
    loading,
    existingTags,
    setNewTag,
    tagTypesConfig,
    removeTag,
    setError,
    addTag,
    setTypedValue,
    clearSearchTerm,
    tagsUpdating,
    setTagsUpdating,
    isMulti,
  });

  const onEditTag = useCallback(
    (tag, tagTypeConfig) => {
      onEditTagAttributes(tag, tagTypeConfig);
    },
    [onEditTagAttributes]
  );

  const onTagClick = useCallback(
    (tag, tagTypeConfig) => {
      if (onEntityTagClick) {
        onEntityTagClick(tag, tagTypeConfig);
      }
    },
    [onEntityTagClick]
  );

  const createNewTag = () => {
    onBlur();
    onCreateNewTag();
  };

  const allowCreateNewTag = canCreateNewTags(tagTypesConfig);

  const customComponents = useMemo(() => {
    const baseComponents: Record<string, unknown> = {
      ...CustomisedComponents,
      ...(!isAssociationTag &&
        !bulkTagMode && {
          MenuList: CustomMenuList,
        }),
    };

    if (hideDropdown) {
      baseComponents["DropdownIndicator"] = () => null;
      baseComponents["IndicatorSeparator"] = () => null;
    }

    return baseComponents;
  }, [isAssociationTag, bulkTagMode, hideDropdown]);

  return {
    createNewTag,
    allowCreateNewTag,
    isMulti,
    options,
    existingTagValuesAsOptions,
    onChange,
    onRemoveTag,
    onEditTag,
    onAddTag,
    onTagClick,
    tagsUpdating,
    openMenu,
    typedValue,
    handleInputChange,
    handleMenuOpen,
    handleMenuClose,
    customComponents,
    applySelectStyles,
  };
};

const TagSelector = (props) => {
  const state = useTagSelectorState(props);
  return <TagSelectorControl {...props} {...state} />;
};

const TagSelectorControl = (p) => (
  <SelectorContext.Provider
    value={{
      createNewTag: p.createNewTag,
      loadMoreLoading: p.loadMoreLoading,
      allowCreateNewTag: p.allowCreateNewTag,
    }}
  >
    <Select {...tagSelectorControlSelectProps(p)} />
  </SelectorContext.Provider>
);

TagSelector.propTypes = {
  loading: PropTypes.bool,
  existingTags: PropTypes.array,
  allTags: PropTypes.array,
  setNewTag: PropTypes.func,
  removeTag: PropTypes.func,
  onBlur: PropTypes.func,
  onEntityTagClick: PropTypes.func,
  showTagTypeHelp: PropTypes.func,
  setError: PropTypes.func,
  tagTypesConfig: PropTypes.arrayOf(TagTypeConfigPropType).isRequired,
  inputValue: PropTypes.string,
  label: PropTypes.string,
  menuIsOpen: PropTypes.bool,
  helpIsOpen: PropTypes.bool,
  isAssociationTag: PropTypes.bool,
  onEditTagAttributes: PropTypes.func,
  selectorRef: PropTypes.object,
  onCreateNewTag: PropTypes.func,
  bulkTagMode: PropTypes.func,
  entityType: PropTypes.string,
  loadOptions: PropTypes.func,
  loadMore: PropTypes.func,
  loadMoreLoading: PropTypes.bool,
  clearSearchTerm: PropTypes.func,
  menuPlacement: PropTypes.string,
  menuPortalTarget: PropTypes.object,
  hideDropdown: PropTypes.bool,
  tagTypes: PropTypes.arrayOf(PropTypes.string),
};

export default TagSelector;
